import React from 'react'
import { Box, Button, Theme, createStyles, makeStyles } from '@material-ui/core';
import DataGrid from "../common/Table/newTable";
import { LateSwapLineup, LateSwapLineupResponse } from './store/types';
import { APIState } from '../utils/api/reducer';
import { useLocalStorage } from 'usehooks-ts';
import { MenuOption } from '../common/selectOptions';
import { lateSwapExposuresDataGridHeaderConfig, lateSwapStackExposuresDataGridHeaderConfig } from '../common/Table/lateSwapConfig';
import { Stack } from '@mui/material';
import FavoriteIcon from '@material-ui/icons/Favorite';
import { CSVLink } from 'react-csv';
import GetAppIcon from "@material-ui/icons/GetApp";
import { connect } from 'react-redux';
import { APIConstants } from '../utils/api/constants';
import { getExposuresKeyLateSwap, getExposuresKeyLineupPlayer } from '../utils/showdownHandler';
import { SwapHoriz } from '@material-ui/icons';
import { SlateResponse } from '../common/store/types';
import { getFilename } from '../common/helpers/FilenameHelper';
import { StackExposures } from '../preContest/queries/queryTypes';
import { showStackExposuresPreContest } from '../utils/stackConstants';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    stepperWrapper: {
      backgroundColor: "#031630",
      borderRadius: "3px 3px 0px 0px",
      color: 'white',
      padding: 12,
      '& .MuiStepLabel-label.MuiStepLabel-alternativeLabel': {
        color: 'rgba(255, 255, 255, 0.60)',
        fontSize: 14,
      },
      '& .MuiStepIcon-root': {
        color: 'rgba(255, 255, 255, 0.38)'
      },
      '& .MuiStepIcon-completed': {
        color: '#009C06',
      },
      '& .MuiStepIcon-active': {
        color: '#0058CC',
      }
    },
    heading: {
      '& .MuiDivider-root': {
        backgroundColor: 'rgba(251, 255, 57, 0.30)',
      }
    },
    generatorSettings: {
      backgroundColor: "#031630",
      padding: 12,
      '& .MuiStepIcon-active': {
        color: '#0058CC',
      }
    },
    active: {
      backgroundColor: "#031630",
    },
    buttonWrapper: {
      paddingRight: "8px",
    },
    exportButton: {
      height: 40,
      borderRadius: 3,
      backgroundColor: '#0042C1',
      textWrap: 'nowrap',
      padding: "10px 16px",
      margin: "4px 0",
      marginLeft: 8,
      "& a": {
        color: "white !important",
        textDecoration: "none",
        display: "flex",
        alignItems: "center",
      },
      "&:hover": {
        color: "white",
        backgroundColor: '#0042C1',
      },
      "& .MuiSvgIcon-root": {
        marginLeft: 4,
        fontSize: "1rem",
      },
    },
    exportButtonCG: {
      height: 40,
      borderRadius: 3,
      textWrap: 'nowrap',
      padding: "10px 16px",
      margin: "4px 0",
      marginLeft: 8,
      "& a": {
        color: "white !important",
        textDecoration: "none",
        display: "flex",
        alignItems: "center",
      },
      "& .MuiSvgIcon-root": {
        marginLeft: 4,
        fontSize: "1rem",
      },
    },
    goToPre: {
      display: "flex",
      padding: "8px 16px",
      justifyContent: "center",
      alignItems: "center",
      gap: "4px",
      color: "white",
      textDecoration: "none",
      backgroundColor: "#0042C1",
      borderRadius: "3px",
      textTransform: "uppercase",
      fontSize: 12,
      fontWeight: 500,
      "& .MuiSvgIcon-root": {
        color: "#7CA9FF !important",
      },
      "&:hover": {
        color: "white",
        backgroundColor: "#0042C1",
        "& .MuiSvgIcon-root": {
          color: "white !important",
        },
      },
    },
    lateSwapOptions: {
      overflowY: 'auto',
    },
    disabled: {
      pointerEvents: "none",
      color: "white !important",
      backgroundColor: "#555555 !important",
      "& .MuiSvgIcon-root": {
        color: "white !important",
      },
    },
    button: {
      borderRadius: 3,
      background: '#0042C1',
      color: 'white',
      padding: '8px 16px',
      marginLeft: 8,
      '&:hover': {
        background: '#0042C1',
      },
      '&:disabled': {
        backgroundColor: '#555555 !important',
        color: '#BEBEBE'
      }
    },
    regenerate: {
      display: "flex",
      justifyContent: "center",
      padding: "8px 16px",
      alignItems: "center",
      gap: "4px",
      backgroundColor: "#008F05",
      paddingRight: "8px",
      color: "white",
      textDecoration: "none",
      borderRadius: "3px",
      textTransform: "uppercase",
      fontSize: 14,
      fontWeight: 500,
      "&:hover": {
        color: "white",
        backgroundColor: "#008F05",
      },
      "&:disabled": {
        color: "white !important",
        backgroundColor: "#555555 !important",
        "& .MuiSvgIcon-root": {
          color: "white !important",
        },
      },
    },
  })
);



interface OwnProps {
  readonly selectedContest: MenuOption<any>;
  readonly exportCsvHeader: string;
}

interface ReduxProps {
  readonly contestLineups: APIState<LateSwapLineupResponse>;
  readonly selectedSlate: SlateResponse;
}

type Props = OwnProps & ReduxProps;


const LateSwapTable = (props: Props) => {
  const classes = useStyles();
  const { contestLineups, selectedContest, selectedSlate, exportCsvHeader } = props;
  const [stackType, setStackType] = React.useState<string>("playerExp");
  const [savedLineups] = useLocalStorage<LateSwapLineup[]>(
    "lateswap-lineups",
    []
  );

  const exposures = () => {
    let players = contestLineups.data.players;
    if (
      savedLineups?.length > 0 &&
      contestLineups.status === APIConstants.SUCCEEDED
    ) {
      const playerCounts: { [key: string]: number } = {};
      savedLineups.forEach((x) =>
        x.fullLineup
          .split(",")
          .forEach(
            (x, index) =>
            (playerCounts[
              getExposuresKeyLineupPlayer(
                x.trim(),
                index,
                selectedSlate?.type,
                selectedSlate?.sport,
                selectedSlate?.site
              )
            ] = playerCounts[
              getExposuresKeyLineupPlayer(
                x.trim(),
                index,
                selectedSlate?.type,
                selectedSlate?.sport,
                selectedSlate?.site
              )
            ]
                ? playerCounts[
                getExposuresKeyLineupPlayer(
                  x.trim(),
                  index,
                  selectedSlate?.type,
                  selectedSlate?.sport,
                  selectedSlate?.site
                )
                ] + 1
                : 1)
          )
      );

      players?.forEach((x) => {
        if (playerCounts[getExposuresKeyLateSwap(x)]) {
          x.postSwapExposure =
            ((playerCounts[getExposuresKeyLateSwap(x)] ?? 0) / savedLineups.length) * 100;
          x.postSwapLeverage =
            (x.postSwapExposure - x.ownership) * 100;
        }
      });
      return players;
    } else if (
      savedLineups?.length === 0 &&
      contestLineups.status === APIConstants.SUCCEEDED
    ) {
      players?.forEach((x) => {
        x.postSwapExposure = x.originalExposure;
        x.postSwapLeverage = 0;
      });
    }
    return [];
  };

  const footer = () => {
    const filterExportData = () => {
      if (savedLineups?.length > 0) {
        let csvData = "";
        const header = exportCsvHeader;
        csvData = header + "\n";
        savedLineups.forEach((x) => {
          if (x.exportableLineup) {
            x?.associatedLateSwapEntries?.filter((entry) => entry.contestName === selectedContest.name || selectedContest.name?.toLowerCase() === 'all').forEach((associatedContestEntry) => {
              if (selectedSlate?.site?.toLowerCase() === 'fd') {
                csvData = csvData.concat(`${associatedContestEntry.entryId},${associatedContestEntry.contestId},${associatedContestEntry.contestName.replace(',', '')},${associatedContestEntry.entryFee.replace(',', '')},` + x.exportableLineup + "\n");
              } else if (selectedSlate?.site?.toLowerCase() === 'ob') {
                csvData = csvData.concat(`${associatedContestEntry.entryId},${associatedContestEntry.contestId},${associatedContestEntry.contestName.replace(',', '')},${associatedContestEntry.entryFee.replace(',', '')},` + x.exportableLineup + "\n");
              }
              else if (selectedSlate?.site?.toLowerCase() === 'yh') {
                csvData = csvData.concat(`${associatedContestEntry.contestId},${associatedContestEntry.entryId},` + x.exportableLineup + "\n");
              }
              else {
                csvData = csvData.concat(`${associatedContestEntry.entryId},${associatedContestEntry.contestName.replace(',', '')},${associatedContestEntry.contestId},${associatedContestEntry.entryFee.replace(',', '')},` + x.exportableLineup + "\n");
              }
            })
          }
        });
        return csvData;
      }
      return "";
    }
    return (
      <Stack spacing={1} direction="row" alignItems='center' display='flex'>
        {savedLineups.length > 0 && (
          <Stack spacing={4} direction="row" alignItems='center' display='flex'>
            <Stack spacing={0.5} direction="row" alignItems='center' display='flex'>
              <Box style={{ height: 18, width: 18, marginRight: 4, }} display="flex">
                <FavoriteIcon
                  style={{
                    width: 18,
                    height: 18,
                    color: '#BB7575'
                  }} />
              </Box>
              {`${savedLineups.length} Lineups Saved`}
            </Stack>
            <Stack spacing={0.5} direction="row" alignItems='center' display='flex'>
              <Box style={{ backgroundColor: '#004803', opacity: 0.7, height: 18, width: 18, marginRight: 4, }} borderRadius="100px" display="flex">
                <SwapHoriz style={{ height: 18, width: 18 }} />
              </Box>
              {`${savedLineups.filter(x => !x.originalLineup).length}/${savedLineups.length} Lineups Swapped`}
            </Stack>
            <Stack spacing={0.5} direction="row" alignItems='center' display='flex'>
              <Box style={{ backgroundColor: 'rgba(251, 255, 57, 0.5)', height: 18, width: 18, marginRight: 4, }} borderRadius="100px" display="flex">
                <Box style={{ color: 'white', justifyContent: 'center', alignItems: 'center', display: 'flex', fontFamily: 'sans-serif', fontWeight: 600, width: '100%' }}>
                  D
                </Box>
              </Box>
              {`${savedLineups.filter(x => savedLineups.filter(y => y.lineupHashCode === x.lineupHashCode).length > 1).length}/${savedLineups.length} Lineups Duped`}
            </Stack>
          </Stack>
        )}
        <div className={classes.buttonWrapper}>
          <Button
            className={classes.exportButton}
            disabled={savedLineups?.length === 0}
          >
            <CSVLink
              data={filterExportData()}
              filename={getFilename(selectedSlate, `_Late_Swap_Lineups`)}
              target="_blank"
            >
              Export Favorites {savedLineups.length > 0 ? getFilteredRowCount() : ''} <GetAppIcon fontSize="small" />
            </CSVLink>
          </Button>
        </div>
      </Stack>
    );
  };

  const getFilteredRowCount = () => {
    let rowCount = savedLineups.filter(
      x => x?.associatedLateSwapEntries !== undefined && x?.associatedLateSwapEntries?.filter(
        (entry) => entry.contestName === selectedContest.name || selectedContest.name?.toLowerCase() === 'all')?.length > 0).length;
    return `(${rowCount})`;
  }
  const stackExposures = () => {
    const stackExposuresDict: { [key: string]: StackExposures } = {};
    const preSwapStackExposuresDict: { [key: string]: StackExposures } = {};
    const savedLineupsCount = savedLineups.length;
    savedLineups.forEach((lineup) => {
      lineup?.stacks.forEach((stack) => {
        var exp = {
          QbName: stack.qbName,
          ThreeMLBOneNFLStackExposureCount: 0,
          FourMLBTwoNFLStackExposureCount: 0,
          FiveMLBThreeNFLStackExposureCount: 0,
        } as StackExposures;
        if (stackExposuresDict[stack.team]) {
          exp = stackExposuresDict[stack.team];
        }
        const stackAndSport = `${stack.count}|${selectedSlate?.sport}`;
        switch (stackAndSport) {
          case "1|NFL":
          case "1|CFB":
          case "3|MLB":
            exp.ThreeMLBOneNFLStackExposureCount =
              exp.ThreeMLBOneNFLStackExposureCount + 1;
            break;
          case "2|NFL":
          case "2|CFB":
          case "4|MLB":
            exp.FourMLBTwoNFLStackExposureCount =
              exp.FourMLBTwoNFLStackExposureCount + 1;
            break;
          case "3|NFL":
          case "3|CFB":
          case "5|MLB":
            exp.FiveMLBThreeNFLStackExposureCount =
              exp.FiveMLBThreeNFLStackExposureCount + 1;
            break;
          default:
            break;
        }
        if (['nfl', 'cfb'].includes(selectedSlate?.sport?.toLowerCase()) || stack.count > 2) {
          stackExposuresDict[stack.team] = exp;
        }
      });
    });
    contestLineups.data.lateSwapData.forEach((lineup) => {
      lineup?.stacks.forEach((stack) => {
        var exp = {
          QbName: stack.qbName,
          ThreeMLBOneNFLStackExposureCount: 0,
          FourMLBTwoNFLStackExposureCount: 0,
          FiveMLBThreeNFLStackExposureCount: 0,
        } as StackExposures;
        if (preSwapStackExposuresDict[stack.team]) {
          exp = preSwapStackExposuresDict[stack.team];
        }
        const stackAndSport = `${stack.count}|${selectedSlate?.sport}`;
        switch (stackAndSport) {
          case "1|NFL":
          case "1|CFB":
          case "3|MLB":
            exp.ThreeMLBOneNFLStackExposureCount =
              exp.ThreeMLBOneNFLStackExposureCount + 1;
            break;
          case "2|NFL":
          case "2|CFB":
          case "4|MLB":
            exp.FourMLBTwoNFLStackExposureCount =
              exp.FourMLBTwoNFLStackExposureCount + 1;
            break;
          case "3|NFL":
          case "3|CFB":
          case "5|MLB":
            exp.FiveMLBThreeNFLStackExposureCount =
              exp.FiveMLBThreeNFLStackExposureCount + 1;
            break;
          default:
            break;
        }
        if (['nfl', 'cfb'].includes(selectedSlate?.sport?.toLowerCase()) || stack.count > 2) {
          preSwapStackExposuresDict[stack.team] = exp;
        }
      });
    });

    if (Object.keys(stackExposuresDict).length > 0) {
      // Build a unique dictionary of team: teamLogo from contestLineups.data.players since we don't have team logos in savedLineups
      const teamLogos: { [key: string]: string } = {};
      contestLineups.data.players?.forEach((player: any) => {
        if (player.team && player.teamLogo && !teamLogos[player.team]) {
          teamLogos[player.team] = player.teamLogo;
        }
      });

      const stacks = Object.keys(stackExposuresDict).map((team) => {
        const stackCounts = stackExposuresDict[team];
        const preSwapStackCounts = preSwapStackExposuresDict[team];
        return {
          id: team,
          team: team,
          teamLogo: teamLogos[team] || '',
          opponentTeamName: '',
          qbName: stackCounts.QbName,
          ThreeMLBOneNFLStackExposureCount:
        stackCounts.ThreeMLBOneNFLStackExposureCount,
          ThreeMLBOneNFLStackExposurePct:
        stackCounts.ThreeMLBOneNFLStackExposureCount / savedLineupsCount,
          FourMLBTwoNFLStackExposureCount:
        stackCounts.FourMLBTwoNFLStackExposureCount,
          FourMLBTwoNFLStackExposurePct:
        stackCounts.FourMLBTwoNFLStackExposureCount / savedLineupsCount,
          FiveMLBThreeNFLStackExposureCount:
        stackCounts.FiveMLBThreeNFLStackExposureCount,
          FiveMLBThreeNFLStackExposurePct:
        stackCounts.FiveMLBThreeNFLStackExposureCount / savedLineupsCount,

          PreSwapThreeMLBOneNFLStackExposureCount:
        preSwapStackCounts?.ThreeMLBOneNFLStackExposureCount,

          PreSwapThreeMLBOneNFLStackExposurePct:
        preSwapStackCounts?.ThreeMLBOneNFLStackExposureCount / contestLineups.data.lateSwapData.length,

          PreSwapFourMLBTwoNFLStackExposureCount:
        preSwapStackCounts?.FourMLBTwoNFLStackExposureCount,

          PreSwapFourMLBTwoNFLStackExposurePct:
        preSwapStackCounts?.FourMLBTwoNFLStackExposureCount / contestLineups.data.lateSwapData.length,

          PreSwapFiveMLBThreeNFLStackExposureCount:
        preSwapStackCounts?.FiveMLBThreeNFLStackExposureCount,
        
          PreSwapFiveMLBThreeNFLStackExposurePct:
        preSwapStackCounts?.FiveMLBThreeNFLStackExposureCount / contestLineups.data.lateSwapData.length,
        } as StackExposures;
      });
      return stacks;
    }

    return [];
  };

  return (
    <Box maxHeight='calc(100vh - 350px)' height='calc(100vh - 350px)'>
      <DataGrid
        includeStacks={showStackExposuresPreContest(selectedSlate)}
        lineups={stackType === "playerExp" ? exposures() : stackExposures()}
        headers={stackType === "playerExp" ?
          lateSwapExposuresDataGridHeaderConfig()
           : lateSwapStackExposuresDataGridHeaderConfig(
          ['nfl', 'cfb'].includes(selectedSlate?.sport?.toLowerCase()),
          contestLineups.data
        )}
        isLoading={false}
        selectedView={stackType}
        setPlayerList={() => console.log("")}
        setStackList={() => console.log("")}
        simType={"lateSwap"}
        setStackView={(view: string) => setStackType(view)}
        pagination
        emptyText="Favorite Lineups to View Exposures."
        fontSize={14}
        footer={(apiRef) => footer()}
        backgroundColor="rgba(219, 77, 0, 0.20)"
      />
    </Box>
  )
}

const mapStateToProps = (state: any) => {
  const {
    contestLineups,
  } = state.lateSwap;

  const {
    selectedSlate,
  } = state.common;

  return {
    contestLineups,
    selectedSlate,
  };
};

const mapDispatchToProps = {
};

export default connect(mapStateToProps, mapDispatchToProps)(LateSwapTable);


